<template>
  <Header />
  <SideBar />
  <main id="main-content" class="ml-64 p-8">
    <!-- Page Header -->
    <div class="mb-8">
      <h2 class="text-2xl font-bold text-green-800">API Requests</h2>
      <p class="mt-1 text-gray-500 text-sm">Audit log of all API calls made through your keys</p>
    </div>

    <!-- Table Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <!-- Loading -->
      <div v-if="apiKeyStore.loading" class="flex justify-center items-center h-48">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>

      <!-- Empty -->
      <div v-else-if="!apiKeyStore.apiRequests?.length" class="p-12 text-center text-gray-400">
        <i class="fa-solid fa-inbox text-4xl mb-3"></i>
        <p class="text-sm">No API requests found</p>
      </div>

      <!-- Table -->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HTTP Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="req in apiKeyStore.apiRequests" :key="req.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">{{ formatDate(req.createdAt) }}</div>
                <div class="text-xs text-gray-400">{{ formatTime(req.createdAt) }}</div>
              </td>
              <td class="px-6 py-4 text-sm font-mono text-gray-700 max-w-xs">
                <span v-if="req.endpoint" :title="req.endpoint" class="block truncate max-w-xs cursor-default">
                  {{ req.endpoint }}
                </span>
                <span v-else class="text-gray-400 italic">No endpoint</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {{ req.orgnizationName || '—' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="statusClass(req.httpStatus)">{{ req.httpStatus || '—' }}</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button @click="openModal(req)"
                  class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50
                  border border-green-200 rounded-md hover:bg-green-100 transition-colors">
                  <i class="fa-solid fa-code mr-1.5"></i>
                  View Payload
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="!apiKeyStore.loading && apiKeyStore.pagination.totalPages > 0" class="px-6 py-4 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-500">
            Page <span class="font-medium text-gray-700">{{ currentPage }}</span>
            of <span class="font-medium text-gray-700">{{ apiKeyStore.pagination.totalPages }}</span>
            &nbsp;·&nbsp;
            <span class="font-medium text-gray-700">{{ apiKeyStore.pagination.totalRows }}</span> total records
          </div>
          <div class="flex items-center space-x-2">
            <button @click="changePage(currentPage - 1)" :disabled="currentPage <= 1"
              class="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700
              hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <i class="fa-solid fa-chevron-left mr-1"></i>Previous
            </button>
            <span class="px-3 py-1.5 bg-green-700 text-white rounded-md text-sm font-medium">
              {{ currentPage }}
            </span>
            <button @click="changePage(currentPage + 1)" :disabled="currentPage >= apiKeyStore.pagination.totalPages"
              class="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700
              hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next<i class="fa-solid fa-chevron-right ml-1"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Request / Response Modal -->
    <Modal :show="showModal" title="Request & Response Payload" size="max-w-3xl" @close="showModal = false">
      <div v-if="selectedRequest" class="space-y-5">
        <!-- Meta row -->
        <div class="flex flex-wrap gap-3 text-xs">
          <span class="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 font-mono">
            <i class="fa-solid fa-clock mr-1.5 text-gray-400"></i>
            {{ formatDate(selectedRequest.createdAt) }} {{ formatTime(selectedRequest.createdAt) }}
          </span>
          <span class="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 font-mono">
            <i class="fa-solid fa-link mr-1.5 text-gray-400"></i>
            {{ selectedRequest.endpoint }}
          </span>
          <span :class="['inline-flex items-center px-2.5 py-1 rounded-md font-semibold', statusClass(selectedRequest.httpStatus)]">
            {{ selectedRequest.httpStatus || '—' }}
          </span>
        </div>

        <!-- Request -->
        <div>
          <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
            <i class="fa-solid fa-arrow-up-from-bracket mr-2 text-blue-400"></i>Request
          </h4>
          <pre class="bg-gray-900 text-green-300 text-xs rounded-md p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-52">{{ prettyJson(selectedRequest.request) }}</pre>
        </div>

        <!-- Response -->
        <div>
          <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
            <i class="fa-solid fa-arrow-down-to-bracket mr-2 text-emerald-400"></i>Response
          </h4>
          <pre class="bg-gray-900 text-green-300 text-xs rounded-md p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-52">{{ prettyJson(selectedRequest.response) }}</pre>
        </div>
      </div>
    </Modal>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useAPIKeysStore } from '@/store/apikeys';
import type { APIRequest } from '@/config/types/responses';
import Header from '../components/header.vue';
import SideBar from '../components/sidebar.vue';
import Modal from '../components/modal.vue';

const apiKeyStore = useAPIKeysStore();

const showModal = ref(false);
const selectedRequest = ref<APIRequest | null>(null);
const currentPage = ref(1);
const PAGE_SIZE = 10;

function loadPage(page: number) {
  const purpose = localStorage.getItem('purpose');
  if (purpose === 'public') {
    apiKeyStore.getAPIRequests(page, PAGE_SIZE);
  } else if (purpose === 'local') {
    apiKeyStore.getAdminAPIRequests(page, PAGE_SIZE);
  }
}

function changePage(page: number) {
  currentPage.value = page;
  loadPage(page);
}

onMounted(() => loadPage(currentPage.value));

function openModal(req: APIRequest) {
  selectedRequest.value = req;
  showModal.value = true;
}

function formatDate(input: string) {
  if (!input) return '—';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
}

function formatTime(input: string) {
  if (!input) return '';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

function prettyJson(raw: string) {
  if (!raw) return '—';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function statusClass(status: string) {
  const code = parseInt(status, 10);
  if (code >= 200 && code < 300) return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700';
  if (code >= 400 && code < 500) return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700';
  if (code >= 500) return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700';
  return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600';
}
</script>
